import useAuth from "@/features/auth/hooks/useAuth";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ChevronRight, LogOut, Menu, PanelLeft } from "lucide-react";
import { useSetAtom } from "jotai";
import { panelAtom } from "@/stores/atoms/panel-atom";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { useActiveNavHref, useSideNavigationLinks } from "@/hooks";
import { ROUTES } from "@/constants/routes";
import { useState } from "react";
import Icon from "./icon";
import UserProfileDropdownButton from "./user-profile-dropdown-button";
import { IconNavSettings } from "./icons";

const MobileNavigation = () => {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const navigationLinks = useSideNavigationLinks();
  const setShowPanel = useSetAtom(panelAtom);
  const activeHref = useActiveNavHref();
  const [drawerOpen, setDrawerOpen] = useState(false);

  return (
    <div className="justify-center items-center flex">
      <Button
        variant="ghost"
        onClick={() => setShowPanel((prev) => !prev)}
        size="icon"
        className="text-gray-500 hidden sm:flex cursor-pointer"
      >
        <PanelLeft />
      </Button>
      <div className="sm:hidden">
        <Drawer direction="left" open={drawerOpen} onOpenChange={setDrawerOpen}>
          <DrawerTrigger asChild>
            <button className="text-gray-400 translate-y-1">
              <Menu />
            </button>
          </DrawerTrigger>
          <DrawerContent className="data-[vaul-drawer-direction=bottom]:max-h-[50vh] data-[vaul-drawer-direction=top]:max-h-[50vh]">
            <DrawerHeader className="pb-3">
              <DrawerTitle className="sr-only">Menu de navigation</DrawerTitle>
              <DrawerDescription className="sr-only">
                Menu de navigation principal avec des actions utilisateur et des
                liens de pages
              </DrawerDescription>
              <button className="bg-[#F7F9FF] w-full cursor-pointer p-3 rounded-lg flex items-center justify-between">
                <UserProfileDropdownButton />
              </button>
            </DrawerHeader>
            <div className="no-scrollbar overflow-y-auto px-4">
              <nav className="my-4 flex flex-col gap-y-2">
                {navigationLinks.map((link) => (
                  <Link
                    key={link.label}
                    to={link.href}
                    onClick={() => setDrawerOpen(false)}
                    className={cn(
                      "flex items-center group justify-between p-3 rounded-md",
                      activeHref === link.href
                        ? "bg-primary/10 font-semibold text-primary"
                        : "text-gray-500 hover:text-primary",
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <Icon icon={link.icon} />
                      <span className="text-sm">{link.label}</span>
                    </div>
                    <ChevronRight
                      className={cn(
                        activeHref !== link.href &&
                          "text-gray-400 group-hover:text-primary",
                      )}
                    />
                  </Link>
                ))}
                <Link
                  to={ROUTES.SETTINGS}
                  onClick={() => setDrawerOpen(false)}
                  className={cn(
                    "flex items-center group justify-between p-3 rounded-md",
                    activeHref === ROUTES.SETTINGS
                      ? "bg-primary/10 font-semibold text-primary"
                      : "text-gray-500 hover:text-primary",
                  )}
                >
                  <div className="flex items-center gap-3 px-1">
                    <IconNavSettings />
                    <span className="text-sm">{t("keys.settings")}</span>
                  </div>
                  <ChevronRight
                    className={cn(
                      activeHref !== ROUTES.SETTINGS &&
                        "text-gray-400 group-hover:text-primary",
                    )}
                  />
                </Link>
              </nav>
            </div>
            <DrawerFooter>
              <button
                onClick={() => {
                  setDrawerOpen(false);
                  logout();
                }}
                className="flex bg-gray-50 rounded-md px-3 items-center justify-between cursor-pointer py-4"
              >
                <div className="flex text-sm items-center gap-2 text-destructive">
                  <LogOut />
                  {t("keys.logout")}
                </div>
                <ChevronRight className="text-gray-400" />
              </button>
            </DrawerFooter>
          </DrawerContent>
        </Drawer>
      </div>
    </div>
  );
};

export default MobileNavigation;
